import React from "react";
import { AbsoluteFill, useCurrentFrame, interpolate, spring, useVideoConfig } from "remotion";

// #115 混合媒体拼贴动画 - 混合 2D/3D 元素、纹理叠加、颗粒噪声
export const MixedMediaCollage: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 颗粒噪声叠加
  const noiseOpacity = interpolate(frame, [0, 60], [0, 0.15], { extrapolateRight: "clamp" });
  
  // 纹理元素动画
  const textureElements = [
    { x: 100, y: 150, size: 200, color: "#ff6b6b", delay: 0, rotation: 5 },
    { x: 600, y: 200, size: 180, color: "#4ecdc4", delay: 10, rotation: -8 },
    { x: 350, y: 400, size: 220, color: "#ffe66d", delay: 20, rotation: 12 },
    { x: 800, y: 350, size: 160, color: "#95e1d3", delay: 30, rotation: -3 },
  ];

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#1a1a2e",
        overflow: "hidden",
      }}
    >
      {/* 背景纹理层 */}
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          background: `
            repeating-linear-gradient(
              45deg,
              rgba(255, 107, 107, 0.1) 0px,
              rgba(255, 107, 107, 0.1) 2px,
              transparent 2px,
              transparent 10px
            )
          `,
          opacity: 0.3,
        }}
      />

      {/* 主要内容层 */}
      {textureElements.map((el, i) => {
        const progress = spring({
          frame: frame - el.delay,
          fps,
          config: { damping: 12, stiffness: 100 },
        });
        
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: el.x,
              top: el.y,
              width: el.size,
              height: el.size,
              background: el.color,
              borderRadius: "20px",
              transform: `
                scale(${progress})
                rotate(${interpolate(progress, [0, 1], [-90, el.rotation])}deg)
                translateY(${interpolate(progress, [0, 1], [200, 0])}px)
              `,
              opacity: progress,
              boxShadow: `0 10px 40px ${el.color}88`,
              mixBlendMode: "multiply",
            }}
          />
        );
      })}

      {/* 3D 层叠文字 */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          fontSize: 120,
          fontWeight: "bold",
          color: "white",
          textShadow: `
            0 1px 0 #ff6b6b,
            0 2px 0 #4ecdc4,
            0 3px 0 #ffe66d,
            0 4px 0 #95e1d3,
            0 20px 30px rgba(0, 0, 0, 0.5)
          `,
          opacity: interpolate(frame, [30, 60], [0, 1], { extrapolateRight: "clamp" }),
        }}
      >
        混合媒体
      </div>

      {/* 颗粒噪声叠加 */}
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          background: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          opacity: noiseOpacity,
          mixBlendMode: "overlay",
        }}
      />

      {/* 边框装饰 */}
      <div
        style={{
          position: "absolute",
          top: 20,
          left: 20,
          right: 20,
          bottom: 20,
          border: "2px solid rgba(255, 255, 255, 0.2)",
          borderRadius: "10px",
        }}
      />
    </AbsoluteFill>
  );
};

// #116 动态排版效果 - 逐字符动画、SVG 路径动画
export const DynamicTypography: React.FC<{ text: string }> = ({ text }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const letters = text.split("");

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0f0f23",
        justifyContent: "center",
        alignItems: "center",
        perspective: "1000px",
      }}
    >
      <div style={{ display: "flex", fontSize: 100, fontWeight: "bold" }}>
        {letters.map((letter, i) => {
          // 错开式进入
          const delay = i * 8;
          const progress = spring({
            frame: frame - delay,
            fps,
            config: { damping: 15, stiffness: 120 },
          });
          
          // 3D 旋转 + 缩放
          const rotateY = interpolate(progress, [0, 1], [90, 0]);
          const scale = interpolate(progress, [0, 1], [0.5, 1]);
          
          // 颜色渐变
          const hue = (i * 360) / letters.length;
          const color = `hsl(${hue}, 70%, 60%)`;
          
          return (
            <div
              key={i}
              style={{
                color,
                transform: `
                  perspective(1000px)
                  rotateY(${rotateY}deg)
                  scale(${scale})
                `,
                opacity: progress,
                textShadow: `0 0 20px ${color}`,
                margin: "0 2px",
              }}
            >
              {letter}
            </div>
          );
        })}
      </div>
      
      {/* SVG 装饰线条 */}
      <svg
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        <path
          d="M 100 300 Q 500 350 900 300"
          fill="none"
          stroke="url(#gradient)"
          strokeWidth="3"
          strokeDasharray="10 5"
          style={{
            strokeDashoffset: interpolate(frame, [0, 100], [200, 0]),
          }}
        />
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ff6b6b" />
            <stop offset="50%" stopColor="#4ecdc4" />
            <stop offset="100%" stopColor="#ffe66d" />
          </linearGradient>
        </defs>
      </svg>
    </AbsoluteFill>
  );
};

// #117 CSS 3D 变换深度效果 - 透视、层叠、3D 旋转
export const CSS3DDepthEffect: React.FC = () => {
  const frame = useCurrentFrame();
  
  const layers = [
    { z: -100, opacity: 0.3, scale: 1.2, color: "#ff6b6b" },
    { z: -50, opacity: 0.5, scale: 1.1, color: "#4ecdc4" },
    { z: 0, opacity: 1, scale: 1, color: "#ffe66d" },
    { z: 50, opacity: 0.8, scale: 0.9, color: "#95e1d3" },
    { z: 100, opacity: 0.6, scale: 0.8, color: "#ff9a9e" },
  ];

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0a0a0a",
        justifyContent: "center",
        alignItems: "center",
        perspective: "1000px",
      }}
    >
      <div
        style={{
          position: "relative",
          width: 500,
          height: 300,
          transformStyle: "preserve-3d",
          transform: `rotateY(${Math.sin(frame * 0.02) * 20}deg) 
                      rotateX(${Math.cos(frame * 0.02) * 10}deg)`,
        }}
      >
        {layers.map((layer, i) => {
          const rotateZ = interpolate(frame, [0, 150], [0 + i * 20, 360 + i * 20]);
          
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                background: `linear-gradient(135deg, ${layer.color}, ${layer.color}88)`,
                borderRadius: "20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: 60,
                fontWeight: "bold",
                color: "white",
                opacity: layer.opacity,
                transform: `
                  translateZ(${layer.z}px)
                  scale(${layer.scale})
                  rotateZ(${rotateZ}deg)
                `,
                boxShadow: `0 10px 40px ${layer.color}44`,
              }}
            >
              Layer {i + 1}
            </div>
          );
        })}
      </div>
      
      {/* 3D 网格地面 */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          width: "100%",
          height: 200,
          background: `
            linear-gradient(0deg, transparent 49%, rgba(255, 255, 255, 0.1) 50%, transparent 51%),
            linear-gradient(90deg, transparent 49%, rgba(255, 255, 255, 0.1) 50%, transparent 51%)
          `,
          backgroundSize: "50px 50px",
          transform: `perspective(500px) rotateX(60deg)`,
          opacity: 0.3,
        }}
      />
    </AbsoluteFill>
  );
};
